import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";
import { lightTheme } from "../styles/themes/light";
import { darkTheme } from "../styles/themes/dark";
import { DefaultTheme } from "styled-components";

type ThemeName = "light" | "dark";

interface ThemeContextValue {
  theme: DefaultTheme;
  themeName: ThemeName;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const getPrimeReactThemeHref = (themeName: ThemeName) =>
  `${process.env.PUBLIC_URL}/primereact-themes/lara-${themeName}-blue/theme.css`;

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const storedTheme = localStorage.getItem("theme") as ThemeName | null;
  const [themeName, setThemeName] = useState<ThemeName>(storedTheme || "light");

  useEffect(() => {
    localStorage.setItem("theme", themeName);
  }, [themeName]);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const themeLink = document.getElementById(
      "primereact-theme"
    ) as HTMLLinkElement | null;

    root.classList.toggle("dark", themeName === "dark");
    body.classList.toggle("dark", themeName === "dark");
    root.dataset.theme = themeName;
    body.dataset.theme = themeName;

    if (themeLink) {
      themeLink.href = getPrimeReactThemeHref(themeName);
    }
  }, [themeName]);

  const theme = themeName === "light" ? lightTheme : darkTheme;

  const toggleTheme = () => {
    setThemeName((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, themeName, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
